/**
 * Copies text to clipboard with iframe fallback support.
 * Tries native clipboard API first, falls back to execCommand for iframe contexts.
 */
export const copyToClipboard = async (text: string, containerElementId?: string): Promise<boolean> => {
  // Try native clipboard API first
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Fallback: Use execCommand with temporary textarea
    // This works in most iframe contexts where clipboard API fails
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      // Use absolute positioning instead of fixed for better dialog/portal compatibility
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      textarea.style.top = '0';
      textarea.style.opacity = '0';
      textarea.style.pointerEvents = 'none';
      textarea.setAttribute('readonly', '');
      textarea.setAttribute('aria-hidden', 'true');
      
      // Try to find the container element first, then fall back to other dialogs or body
      const resetPasswordDialog = containerElementId ? document.getElementById(containerElementId) : null;
      const container = resetPasswordDialog ?? document.body;
      container.appendChild(textarea);
      
      // For better compatibility in dialogs/portals, ensure the textarea is selectable
      const isIOS = /ipad|ipod|iphone/i.exec(navigator.userAgent) !== null;
      if (isIOS) {
        // iOS requires contentEditable
        textarea.contentEditable = 'true';
        textarea.readOnly = false;
        const range = document.createRange();
        range.selectNodeContents(textarea);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
        textarea.setSelectionRange(0, 999999);
      } else {
        textarea.select();
        textarea.setSelectionRange(0, 999999);
      }

      // Note: execCommand is deprecated but used as fallback for contexts where clipboard API fails (iframes, some dialogs)
      // eslint-disable-next-line @typescript-eslint/no-deprecated -- Intentional fallback
      const success = document.execCommand('copy');
      container.removeChild(textarea);
      return success;
    } catch {
      return false;
    }
  }
};
